import {useIntl, defineMessages, FormattedMessage} from 'react-intl';
import {getStore} from 'src/v2/state/store';
import {EVM_NETWORKS} from 'src/types';
import type {NetworkType} from 'src/v2/state/types';
import {getNetworkName} from 'src/components/Wallet/constants';
import {Text} from 'src/v2/components/common/Text';
import {COLORS} from 'src/v2/util/color';
import {GaslessBadge} from 'src/v2/components/walletConnect/GaslessBadge';
import {useSessionContext} from 'src/state/usePayinSessionContext';

const messages = defineMessages({
  noGasFeesOnNetworks: {
    id: 'crypto.payins.v2.gasless_promotion.no_gas_fees_on_networks',
    description:
      'Text shown when some EVM networks support gasless payments, with a list of network names',
    defaultMessage: '<green>No gas fees</green> on {networks}',
  },
  noGasFeesForNonSmartWalletsOnNetworks: {
    id: 'crypto.payins.v2.gasless_promotion.no_gas_fees_for_non_smart_wallets_on_networks',
    description:
      'Text shown when some EVM networks support gasless payments for non-smart wallets, with a list of network names',
    defaultMessage:
      '<green>No gas fees for non-smart wallets</green> on {networks}',
  },
});

export const GaslessPromotion = ({
  networkType,
  walletProvider,
}: {
  networkType: NetworkType;
  walletProvider?: string;
}) => {
  const intl = useIntl();
  const store = getStore();
  const {mode} = useSessionContext();

  const setupFutureUsage = store.useSelector((state) => state.setupFutureUsage);
  const enableGaslessV2 = store.useSelector(
    (state) => state.featureFlags.enable_gasless_v2,
  );
  const enableSmartContractWalletDetection = store.useSelector(
    (state) => state.featureFlags.enable_smart_contract_wallet_detection,
  );
  const spenderContractAddresses = store.useSelector(
    (state) => state.spenderContractAddresses,
  );
  const supportedCurrencies = store.useSelector(
    (state) => state.supportedCurrencies,
  );

  // setup sessions and SFU are inherently gasless, no need for extra promotion
  if (
    mode === 'setup' ||
    setupFutureUsage ||
    networkType !== 'evm' ||
    !enableGaslessV2
  ) {
    return null;
  }

  const supportedEvmNetworks = [
    ...new Set(
      supportedCurrencies
        .map((c) => c.currencyNetwork)
        .filter((n) => EVM_NETWORKS.includes(n)),
    ),
  ];

  if (supportedEvmNetworks.length === 0) {
    return null;
  }

  const spenderKeys = Object.keys(spenderContractAddresses);
  const gaslessNetworks = supportedEvmNetworks.filter((network) =>
    spenderKeys.some((key) => key.endsWith(`.${network}`)),
  );

  if (gaslessNetworks.length === 0) {
    return null;
  }

  if (gaslessNetworks.length === supportedEvmNetworks.length) {
    return <GaslessBadge walletProvider={walletProvider} />;
  }

  const showSmartWalletDisclaimer =
    walletProvider === 'Coinbase Wallet' && enableSmartContractWalletDetection;

  const networkNames = gaslessNetworks.map((n) => getNetworkName(n));

  return (
    <Text type="captionEmphasized" css={{color: COLORS.textSubduedOnLight}}>
      <FormattedMessage
        {...(showSmartWalletDisclaimer
          ? messages.noGasFeesForNonSmartWalletsOnNetworks
          : messages.noGasFeesOnNetworks)}
        values={{
          networks: intl.formatList(networkNames),
          green: (chunks) => (
            <span style={{color: COLORS.textFeedbackSuccess}}>{chunks}</span>
          ),
        }}
      />
    </Text>
  );
};
